import type {
  AcademicYear,
  Application,
  ApplicationDocument,
  ApplicationNote,
  ApplicationStatus,
  ChildApplication,
  Email,
  EnrollmentStepData,
  EnrollmentProgress,
  GradeLevel,
  GrandparentInfo,
  MedicalRecord,
  ParentInfo,
  ParentRelation,
  PhoneNumber,
  SchoolDivision,
  StudentGender,
  SubmitApplicationInput,
  UploadDocumentInput,
} from '@triedge/admissions-domain';
import { ResourceId } from '@triedge/domain-core/types';
import {
  type UiAcceptanceLetterStepData,
  type UiApplication,
  type UiApplicationStatus,
  type UiDocument,
  type UiEnrollmentProgress,
  type UiEnrollmentStepData,
  type UiFormFamily,
  type UiFormStudent,
  type UiGrandparentInfo,
  type UiMedicalRecord,
  type UiNote,
  type UiParent,
  type UiRegistrationFeeStepData,
  type UiStudent,
  type UiTransportationB6tStepData,
  type UiTuitionContractStepData,
} from './ui-types';
import { toIsoDate, toIsoDateTime, toUnixSeconds } from '../shared/timestamp-mappers';

export { toUnixSeconds } from '../shared/timestamp-mappers';

const STATUS_TO_DOMAIN: Record<UiApplicationStatus, ApplicationStatus> = {
  Draft: 'draft',
  Submitted: 'submitted',
  'Under Review': 'under_review',
  'Interview Scheduled': 'interview_scheduled',
  'Decision Pending': 'decision_pending',
  Accepted: 'accepted',
  'Accepted - Pending Enrollment': 'accepted_pending_enrollment',
  'Acceptance Expired': 'acceptance_expired',
  Waitlisted: 'waitlisted',
  Denied: 'denied',
  Enrolled: 'enrolled',
  Withdrawn: 'withdrawn',
};

const STATUS_TO_UI: Record<ApplicationStatus, UiApplicationStatus> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  interview_scheduled: 'Interview Scheduled',
  decision_pending: 'Decision Pending',
  accepted: 'Accepted',
  accepted_pending_enrollment: 'Accepted - Pending Enrollment',
  acceptance_expired: 'Acceptance Expired',
  waitlisted: 'Waitlisted',
  denied: 'Denied',
  enrolled: 'Enrolled',
  withdrawn: 'Withdrawn',
};

const GENDER_TO_DOMAIN: Record<string, StudentGender> = {
  Male: 'male',
  Female: 'female',
  Other: 'other',
  Boys: 'male',
  Girls: 'female',
};

const GENDER_TO_UI: Record<StudentGender, string> = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
};

const RELATION_TO_UI: Record<ParentRelation, UiParent['relation']> = {
  father: 'Father',
  mother: 'Mother',
  guardian: 'Guardian',
};

const IMMUNIZATION_TO_DOMAIN: Record<string, MedicalRecord['immunizationStatus']> = {
  'Up to Date': 'up_to_date',
  Partial: 'partial',
  Exempt: 'exempt',
  'Not Provided': 'not_provided',
  Unknown: 'not_provided',
};

const IMMUNIZATION_TO_UI: Record<MedicalRecord['immunizationStatus'], UiMedicalRecord['immunizationStatus']> = {
  up_to_date: 'Up to Date',
  partial: 'Partial',
  exempt: 'Exempt',
  not_provided: 'Not Provided',
};

const GRADE_TO_DOMAIN: Record<string, GradeLevel> = {
  Nursery: 'nursery',
  'Pre-K': 'pre_k',
  Kindergarten: 'kindergarten',
  '1': 'grade_1',
  '2': 'grade_2',
  '3': 'grade_3',
  '4': 'grade_4',
  '5': 'grade_5',
  '6': 'grade_6',
  '7': 'grade_7',
  '8': 'grade_8',
};

const GRADE_TO_UI: Record<GradeLevel, string> = {
  nursery: 'Nursery',
  pre_k: 'Pre-K',
  kindergarten: 'Kindergarten',
  grade_1: '1',
  grade_2: '2',
  grade_3: '3',
  grade_4: '4',
  grade_5: '5',
  grade_6: '6',
  grade_7: '7',
  grade_8: '8',
};

const asAcademicYear = (value: string): AcademicYear => value as AcademicYear;
const asEmail = (value: string): Email => value as Email;
const asPhoneNumber = (value: string): PhoneNumber => value as PhoneNumber;

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function splitFullName(value: string): { firstName: string; lastName: string } {
  const trimmed = normalizeWhitespace(value);
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(' ');
  if (parts.length === 1) return { firstName: parts[0] ?? '', lastName: '' };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1] ?? '',
  };
}

function inferDivision(gradeApplying: string, gender: string): SchoolDivision {
  const normalizedGrade = normalizeUiGrade(gradeApplying).toLowerCase();
  const lowerGrade = normalizedGrade.toLowerCase();
  if (lowerGrade.includes('nursery') || lowerGrade.includes('pre') || lowerGrade.includes('kindergarten')) {
    return 'preschool';
  }
  if (gender === 'Male') return 'boys';
  if (gender === 'Female') return 'girls';
  return 'preschool';
}

function normalizeUiGrade(value: string): string {
  const trimmed = value.trim();
  const upper = trimmed.toUpperCase();
  if (upper === 'K' || upper === 'KINDERGARTEN') {
    return 'Kindergarten';
  }
  if (upper === 'N' || upper === 'N3' || upper === 'N4' || upper === 'NURSERY') {
    return 'Nursery';
  }
  if (upper === 'PRE-K' || upper === 'PRE K' || upper === 'PREK' || upper === 'PK') {
    return 'Pre-K';
  }
  return trimmed;
}


function mapGrandparent(info?: UiFormFamily['paternalGrandparents']): GrandparentInfo | undefined {
  if (!info) return undefined;
  const grandfather = normalizeWhitespace(`${info.grandfatherTitle} ${info.grandfatherName}`.trim());
  const grandmother = normalizeWhitespace(`${info.grandmotherTitle} ${info.grandmotherName}`.trim());
  const names = [grandfather, grandmother].filter(Boolean).join(' & ');
  const location = normalizeWhitespace(`${info.city} ${info.state}`.trim());
  return {
    names,
    location,
    phone: info.phone || undefined,
    family: info.lastName || undefined,
  };
}

function mapGrandparentToUi(info?: GrandparentInfo | null): UiGrandparentInfo | undefined {
  if (!info) return undefined;
  const result: UiGrandparentInfo = {
    names: info.names,
    location: info.location,
  };
  if (info.phone) {
    result.phone = info.phone;
  }
  if (info.family) {
    result.family = info.family;
  }
  return result;
}

function mapParent(parent: ParentInfo): UiParent {
  const result: UiParent = {
    firstName: parent.firstName,
    lastName: parent.lastName,
    email: parent.email ?? '',
    phone: parent.phone ?? '',
    relation: parent.relation ? RELATION_TO_UI[parent.relation] : 'Guardian',
  };
  if (parent.title) result.title = parent.title;
  if (parent.occupation) result.occupation = parent.occupation;
  if (parent.firm) result.firm = parent.firm;
  if (parent.isPrimary !== undefined) result.isPrimary = parent.isPrimary;
  if (parent.maidenName) result.maidenName = parent.maidenName;
  return result;
}

function mapMedicalRecord(record?: MedicalRecord | null): UiMedicalRecord | undefined {
  if (!record) return undefined;
  return {
    allergies: record.allergies,
    chronicConditions: record.chronicConditions,
    medications: record.medications,
    immunizationStatus: IMMUNIZATION_TO_UI[record.immunizationStatus],
    emergencyNotes: record.emergencyNotes,
  };
}

function mapEnrollmentProgress(progress?: EnrollmentProgress | null): UiEnrollmentProgress | undefined {
  if (!progress) return undefined;
  const result: UiEnrollmentProgress = {
    step1AcceptanceLetter: progress.step1AcceptanceLetter,
    step2RegistrationForm: progress.step2RegistrationForm,
    step3B6TForm: progress.step3B6TForm,
    step4TuitionContract: progress.step4TuitionContract,
    step5Payment: progress.step5Payment,
    step6ImmunizationRecords: progress.step6ImmunizationRecords,
    immunizationVerified: progress.immunizationVerified,
  };
  const formSubmittedAt = toIsoDateTime(progress.formSubmittedAt);
  if (formSubmittedAt) result.formSubmittedAt = formSubmittedAt;
  const contractSignedAt = toIsoDateTime(progress.contractSignedAt);
  if (contractSignedAt) result.contractSignedAt = contractSignedAt;
  const paymentConfirmedAt = toIsoDateTime(progress.paymentConfirmedAt);
  if (paymentConfirmedAt) result.paymentConfirmedAt = paymentConfirmedAt;
  return result;
}

export const mapDomainMedicalToUi = mapMedicalRecord;
export const mapDomainEnrollmentToUi = mapEnrollmentProgress;

function mapEnrollmentStepData(stepData?: EnrollmentStepData | null): UiEnrollmentStepData | undefined {
  if (!stepData) return undefined;
  const mapped: UiEnrollmentStepData = {};
  if (stepData.acceptanceLetter) {
    const acceptanceLetter: UiAcceptanceLetterStepData = {};
    const reviewedAt = toIsoDateTime(stepData.acceptanceLetter.reviewedAt);
    if (reviewedAt) acceptanceLetter.reviewedAt = reviewedAt;
    if (stepData.acceptanceLetter.acknowledgedBy !== undefined) {
      acceptanceLetter.acknowledgedBy = stepData.acceptanceLetter.acknowledgedBy;
    }
    if (stepData.acceptanceLetter.notes != null) acceptanceLetter.notes = stepData.acceptanceLetter.notes;
    if (stepData.acceptanceLetter.documentId != null) acceptanceLetter.documentId = stepData.acceptanceLetter.documentId;
    if (stepData.acceptanceLetter.documentResourceId != null) {
      acceptanceLetter.documentResourceId = stepData.acceptanceLetter.documentResourceId;
    }
    mapped.acceptanceLetter = acceptanceLetter;
  }
  if (stepData.tuitionContract) {
    const tuitionContract: UiTuitionContractStepData = {
      termsAcknowledged: stepData.tuitionContract.termsAcknowledged,
    };
    if (stepData.tuitionContract.contractVersion != null) {
      tuitionContract.contractVersion = stepData.tuitionContract.contractVersion;
    }
    if (stepData.tuitionContract.signerFullName != null) {
      tuitionContract.signerFullName = stepData.tuitionContract.signerFullName;
    }
    const signedAt = toIsoDateTime(stepData.tuitionContract.signedAt);
    if (signedAt) tuitionContract.signedAt = signedAt;
    if (stepData.tuitionContract.signatureToken != null) {
      tuitionContract.signatureToken = stepData.tuitionContract.signatureToken;
    }
    if (stepData.tuitionContract.totalDueCents != null) {
      tuitionContract.totalDueCents = stepData.tuitionContract.totalDueCents;
    }
    if (stepData.tuitionContract.installmentCount != null) {
      tuitionContract.installmentCount = stepData.tuitionContract.installmentCount;
    }
    if (stepData.tuitionContract.paymentMethod != null) {
      tuitionContract.paymentMethod = stepData.tuitionContract.paymentMethod;
    }
    if (stepData.tuitionContract.paymentPreferenceLabel != null) {
      tuitionContract.paymentPreferenceLabel = stepData.tuitionContract.paymentPreferenceLabel;
    }
    if (stepData.tuitionContract.paymentAmountLabel != null) {
      tuitionContract.paymentAmountLabel = stepData.tuitionContract.paymentAmountLabel;
    }
    if (stepData.tuitionContract.installmentAmountLabel != null) {
      tuitionContract.installmentAmountLabel = stepData.tuitionContract.installmentAmountLabel;
    }
    if (stepData.tuitionContract.installmentStartDate != null) {
      tuitionContract.installmentStartDate = stepData.tuitionContract.installmentStartDate;
    }
    if (stepData.tuitionContract.installmentEndDate != null) {
      tuitionContract.installmentEndDate = stepData.tuitionContract.installmentEndDate;
    }
    if (stepData.tuitionContract.thirdPartyName != null) {
      tuitionContract.thirdPartyName = stepData.tuitionContract.thirdPartyName;
    }
    if (stepData.tuitionContract.documentId != null) {
      tuitionContract.documentId = stepData.tuitionContract.documentId;
    }
    if (stepData.tuitionContract.documentResourceId != null) {
      tuitionContract.documentResourceId = stepData.tuitionContract.documentResourceId;
    }
    mapped.tuitionContract = tuitionContract;
  }
  if (stepData.transportationB6t) {
    const transportationB6t: UiTransportationB6tStepData = {
      requiresTransportation: stepData.transportationB6t.requiresTransportation,
      amPickupMode: stepData.transportationB6t.amPickupMode,
      pmDropoffMode: stepData.transportationB6t.pmDropoffMode,
    };
    if (stepData.transportationB6t.amPickupAddress != null) {
      transportationB6t.amPickupAddress = stepData.transportationB6t.amPickupAddress;
    }
    if (stepData.transportationB6t.pmDropoffAddress != null) {
      transportationB6t.pmDropoffAddress = stepData.transportationB6t.pmDropoffAddress;
    }
    if (stepData.transportationB6t.specialInstructions != null) {
      transportationB6t.specialInstructions = stepData.transportationB6t.specialInstructions;
    }
    if (stepData.transportationB6t.signerFullName != null) {
      transportationB6t.signerFullName = stepData.transportationB6t.signerFullName;
    }
    const signedAt = toIsoDateTime(stepData.transportationB6t.signedAt);
    if (signedAt) transportationB6t.signedAt = signedAt;
    mapped.transportationB6t = transportationB6t;
  }
  if (stepData.registrationFee) {
    const registrationFee: UiRegistrationFeeStepData = {
      amountPaidCents: stepData.registrationFee.amountPaidCents,
      currency: stepData.registrationFee.currency,
      paymentProvider: stepData.registrationFee.paymentProvider,
    };
    if (stepData.registrationFee.transactionReference != null) {
      registrationFee.transactionReference = stepData.registrationFee.transactionReference;
    }
    if (stepData.registrationFee.receiptDocumentId != null) {
      registrationFee.receiptDocumentId = stepData.registrationFee.receiptDocumentId;
    }
    const paidAt = toIsoDateTime(stepData.registrationFee.paidAt);
    if (paidAt) registrationFee.paidAt = paidAt;
    mapped.registrationFee = registrationFee;
  }
  return Object.keys(mapped).length > 0 ? mapped : undefined;
}

function mapStudent(
  child: ChildApplication,
  fallbackStatus: ApplicationStatus,
  fallbackAssignee?: string | null
): UiStudent {
  const info = child.childInfo;
  const student: UiStudent = {
    id: child.id,
    firstName: info.firstName,
    lastName: info.lastName,
    dob: toIsoDate(info.dateOfBirth) ?? '',
    gender: GENDER_TO_UI[info.gender ?? 'other'] as UiStudent['gender'],
    gradeApplying: GRADE_TO_UI[child.gradeApplyingFor],
    status: STATUS_TO_UI[child.status ?? fallbackStatus],
  };
  if (info.middleName) student.middleName = info.middleName;
  if (info.hebrewFirstName) student.hebrewFirstName = info.hebrewFirstName;
  if (info.hebrewMiddleName) student.hebrewMiddleName = info.hebrewMiddleName;
  if (info.hebrewLastName) student.hebrewLastName = info.hebrewLastName;
  if (info.calledBy) student.calledBy = info.calledBy;
  if (info.hebrewDateOfBirth) student.hebrewDob = info.hebrewDateOfBirth;
  const currentGrade = info.currentGrade ? GRADE_TO_UI[info.currentGrade] : undefined;
  if (currentGrade) student.currentGrade = currentGrade;
  const medical = mapMedicalRecord(child.medical);
  if (medical) student.medical = medical;
  const assignedTo = child.assignedTo ?? fallbackAssignee ?? undefined;
  if (assignedTo) student.assignedTo = assignedTo;
  const enrollmentDeadline = toIsoDate(child.enrollmentDeadline);
  if (enrollmentDeadline) student.enrollmentDeadline = enrollmentDeadline;
  const enrollmentProgress = mapEnrollmentProgress(child.enrollmentProgress);
  if (enrollmentProgress) student.enrollmentProgress = enrollmentProgress;
  const enrollmentStepData = mapEnrollmentStepData(child.enrollmentStepData);
  if (enrollmentStepData) student.enrollmentStepData = enrollmentStepData;
  if (info.placeOfBirth) student.placeOfBirth = info.placeOfBirth;
  if (info.languageAtHome) student.languageAtHome = info.languageAtHome;
  const currentSchool = info.currentSchool ?? child.previousSchool ?? undefined;
  if (currentSchool) student.currentSchool = currentSchool;
  if (info.principalName) student.principalName = info.principalName;
  if (info.principalPhone) student.principalPhone = info.principalPhone;
  if (child.healthInfo?.physicianName) student.pediatricianName = child.healthInfo.physicianName;
  if (child.healthInfo?.physicianPhone) student.pediatricianPhone = child.healthInfo.physicianPhone;
  if (child.healthInfo?.dentistName) student.dentistName = child.healthInfo.dentistName;
  if (child.healthInfo?.dentistPhone) student.dentistPhone = child.healthInfo.dentistPhone;
  return student;
}

function mapNote(note: ApplicationNote): UiNote {
  return {
    id: note.id,
    text: note.content,
    author: note.authorName ?? note.authorId,
    timestamp: toIsoDateTime(note.createdAt) ?? '',
    isImportant: note.isImportant ?? false,
  };
}

function mapDocument(doc: ApplicationDocument): UiDocument {
  const mapped: UiDocument = {
    id: doc.id,
    resourceId: doc.resourceId,
  };
  if (doc.fileName) mapped.name = doc.fileName;
  if (doc.contentType) mapped.contentType = doc.contentType;
  if (typeof doc.sizeBytes === 'number') mapped.sizeBytes = doc.sizeBytes;
  if (Array.isArray(doc.childApplicationIds) && doc.childApplicationIds.length > 0) {
    mapped.childApplicationIds = [...doc.childApplicationIds];
  }
  if (doc.storageProvider) mapped.storageProvider = doc.storageProvider;
  if (doc.storageKey) mapped.storageKey = doc.storageKey;
  if (doc.storageBucket) mapped.storageBucket = doc.storageBucket;
  if (doc.storageRegion) mapped.storageRegion = doc.storageRegion;
  return mapped;
}

export function toDomainStatus(status: UiApplicationStatus): ApplicationStatus {
  return STATUS_TO_DOMAIN[status];
}

export function toDomainGradeLevel(value?: string): GradeLevel | null {
  if (!value) return null;
  const normalized = normalizeUiGrade(value);
  return GRADE_TO_DOMAIN[normalized] ?? null;
}

export function toUiGradeLabel(value: string): string {
  return GRADE_TO_UI[value as GradeLevel] ?? value;
}

export function toUiStatus(status: ApplicationStatus): UiApplicationStatus {
  return STATUS_TO_UI[status];
}

export function mapDomainApplicationToUi(application: Application): UiApplication {
  const applicant = application.applicant;
  const address = applicant?.primaryContact?.address;
  const ui: UiApplication = {
    id: application.id,
    referenceCode: application.code,
    familyName: applicant?.familyName ?? '',
    address: address?.street ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zip: address?.zipCode ?? '',
    homePhone: applicant?.homePhone ?? '',
    students: application.children.map((child) => mapStudent(child, application.status, application.assignedTo)),
    parents: (applicant?.parents ?? []).map(mapParent),
    submittedAt: toIsoDateTime(application.submittedAt) ?? '',
    status: STATUS_TO_UI[application.status],
    notes: (application.notes ?? []).map(mapNote),
    documents: (application.documents ?? []).map(mapDocument),
  };
  if (application.assignedTo) ui.assignedTo = application.assignedTo;
  if (applicant?.synagogue) ui.synagogue = applicant.synagogue;
  if (applicant?.rav) ui.rav = applicant.rav;
  if (applicant?.maritalStatus) ui.maritalStatus = applicant.maritalStatus;
  if (applicant?.adoptedChild) ui.adoptedChild = applicant.adoptedChild;
  if (applicant?.stepParent) ui.stepParent = applicant.stepParent;
  const interviewDate = toIsoDateTime(application.interviewDate);
  if (interviewDate) ui.interviewDate = interviewDate;
  const visitDate = toIsoDateTime(application.visitDate);
  if (visitDate) ui.visitDate = visitDate;
  const paternal = mapGrandparentToUi(applicant?.paternalGrandparents);
  if (paternal) ui.paternalGrandparents = paternal;
  const maternal = mapGrandparentToUi(applicant?.maternalGrandparents);
  if (maternal) ui.maternalGrandparents = maternal;
  return ui;
}

export function mapFormToSubmitInput(
  family: UiFormFamily,
  students: UiFormStudent[],
  academicYear: string
): SubmitApplicationInput {
  const father = splitFullName(family.fatherName);
  const mother = splitFullName(family.motherName);
  const primaryContact = family.fatherEmail || family.fatherCell
    ? {
        firstName: father.firstName || family.fatherName,
        lastName: father.lastName,
        email: family.fatherEmail,
        phone: family.fatherCell,
      }
    : {
        firstName: mother.firstName || family.motherName,
        lastName: mother.lastName,
        email: family.motherEmail,
        phone: family.motherCell,
      };
  const primaryEmail = primaryContact.email || family.motherEmail || family.fatherEmail;
  const primaryPhone = primaryContact.phone || family.motherCell || family.fatherCell || family.homePhone;
  const toOptionalEmail = (value: string): Email | undefined => (value ? asEmail(value) : undefined);
  const toOptionalPhone = (value: string): PhoneNumber | undefined => (value ? asPhoneNumber(value) : undefined);

  return {
    applicant: {
      familyName: family.surname,
      primaryContact: {
        firstName: primaryContact.firstName,
        lastName: primaryContact.lastName,
        email: asEmail(primaryEmail),
        phone: asPhoneNumber(primaryPhone),
        address: {
          street: family.address,
          city: family.city,
          state: family.state,
          zipCode: family.zip,
        },
      },
      homePhone: toOptionalPhone(family.homePhone),
      parents: [
        {
          title: family.fatherTitle || undefined,
          firstName: father.firstName || family.fatherName,
          lastName: father.lastName,
          email: toOptionalEmail(family.fatherEmail),
          phone: toOptionalPhone(family.fatherCell),
          relation: 'father',
          occupation: family.fatherOccupation || undefined,
          firm: family.fatherFirm || undefined,
          isPrimary: true,
          isDeceasedOrNoLongerLegalGuardian: false,
        },
        {
          title: family.motherTitle || undefined,
          firstName: mother.firstName || family.motherName,
          lastName: mother.lastName,
          email: toOptionalEmail(family.motherEmail),
          phone: toOptionalPhone(family.motherCell),
          relation: 'mother',
          occupation: family.motherOccupation || undefined,
          firm: family.motherFirm || undefined,
          isPrimary: true,
          isDeceasedOrNoLongerLegalGuardian: false,
          maidenName: family.motherMaidenName || undefined,
        },
      ],
      synagogue: family.shul || undefined,
      rav: family.rav || undefined,
      maritalStatus: family.maritalStatus || undefined,
      adoptedChild: family.isAdopted || undefined,
      stepParent: family.isStepParent || undefined,
      paternalGrandparents: mapGrandparent(family.paternalGrandparents),
      maternalGrandparents: mapGrandparent(family.maternalGrandparents),
    },
    academicYear: asAcademicYear(academicYear),
    children: students.map((student) => {
      const birthUnix = toUnixSeconds(student.dob) ?? Math.floor(Date.now() / 1000);
      const normalizedCurrentGrade = student.currentGrade ? normalizeUiGrade(student.currentGrade) : '';
      const normalizedGradeApplying = normalizeUiGrade(student.grade);
      const currentGrade = GRADE_TO_DOMAIN[normalizedCurrentGrade] ?? undefined;
      const gradeApplying = GRADE_TO_DOMAIN[normalizedGradeApplying] ?? 'kindergarten';
      return {
        childInfo: {
          firstName: student.firstName,
          middleName: student.middleName || undefined,
          lastName: student.lastName,
          hebrewFirstName: student.hebrewFirstName || undefined,
          hebrewMiddleName: student.hebrewMiddleName || undefined,
          hebrewLastName: student.hebrewLastName || undefined,
          calledBy: student.calledBy || undefined,
          dateOfBirth: birthUnix,
          hebrewDateOfBirth: student.hebrewDob || undefined,
          gender: GENDER_TO_DOMAIN[student.gender] ?? 'other',
          currentGrade,
          placeOfBirth: student.placeOfBirth || undefined,
          languageAtHome: student.languageAtHome || undefined,
          currentSchool: student.currentSchool || undefined,
          principalName: student.principalName || undefined,
          principalPhone: student.principalPhone || undefined,
        },
        gradeApplyingFor: gradeApplying,
        schoolDivision: inferDivision(student.grade, student.gender),
        previousSchool: student.currentSchool || undefined,
        healthInfo: {
          allergies: [],
          medications: [],
          specialNeeds: undefined,
          physicianName: student.pediatricianName || undefined,
          physicianPhone: student.pediatricianPhone || undefined,
          dentistName: student.dentistName || undefined,
          dentistPhone: student.dentistPhone || undefined,
          insuranceProvider: undefined,
          insurancePolicyNumber: undefined,
        },
      };
    }),
  };
}

export function mapUiMedicalToDomain(record?: UiMedicalRecord): MedicalRecord | undefined {
  if (!record) return undefined;
  return {
    allergies: record.allergies ?? [],
    chronicConditions: record.chronicConditions ?? [],
    medications: record.medications ?? [],
    immunizationStatus: IMMUNIZATION_TO_DOMAIN[record.immunizationStatus] ?? 'not_provided',
    emergencyNotes: record.emergencyNotes ?? '',
  };
}

export function mapUiEnrollmentToDomain(progress?: UiEnrollmentProgress): EnrollmentProgress | undefined {
  if (!progress) return undefined;
  return {
    step1AcceptanceLetter: progress.step1AcceptanceLetter,
    step2RegistrationForm: progress.step2RegistrationForm,
    step3B6TForm: progress.step3B6TForm,
    step4TuitionContract: progress.step4TuitionContract,
    step5Payment: progress.step5Payment,
    step6ImmunizationRecords: progress.step6ImmunizationRecords,
    formSubmittedAt: toUnixSeconds(progress.formSubmittedAt) ?? undefined,
    contractSignedAt: toUnixSeconds(progress.contractSignedAt) ?? undefined,
    paymentConfirmedAt: toUnixSeconds(progress.paymentConfirmedAt) ?? undefined,
    immunizationVerified: progress.immunizationVerified ?? false,
  };
}

export function mapUiEnrollmentStepDataToDomain(
  stepData?: UiEnrollmentStepData
): EnrollmentStepData | undefined {
  if (!stepData) return undefined;
  const mapped: EnrollmentStepData = {};
  if (stepData.acceptanceLetter) {
    mapped.acceptanceLetter = {
      reviewedAt: toUnixSeconds(stepData.acceptanceLetter.reviewedAt) ?? undefined,
      acknowledgedBy: stepData.acceptanceLetter.acknowledgedBy,
      notes: stepData.acceptanceLetter.notes ?? undefined,
      documentId: stepData.acceptanceLetter.documentId ?? undefined,
      documentResourceId: stepData.acceptanceLetter.documentResourceId ?? undefined,
    };
  }
  if (stepData.tuitionContract) {
    mapped.tuitionContract = {
      contractVersion: stepData.tuitionContract.contractVersion ?? undefined,
      signerFullName: stepData.tuitionContract.signerFullName ?? undefined,
      signedAt: toUnixSeconds(stepData.tuitionContract.signedAt) ?? undefined,
      signatureToken: stepData.tuitionContract.signatureToken ?? undefined,
      totalDueCents: stepData.tuitionContract.totalDueCents ?? undefined,
      installmentCount: stepData.tuitionContract.installmentCount ?? undefined,
      paymentMethod: stepData.tuitionContract.paymentMethod ?? undefined,
      paymentPreferenceLabel: stepData.tuitionContract.paymentPreferenceLabel ?? undefined,
      paymentAmountLabel: stepData.tuitionContract.paymentAmountLabel ?? undefined,
      installmentAmountLabel: stepData.tuitionContract.installmentAmountLabel ?? undefined,
      installmentStartDate: stepData.tuitionContract.installmentStartDate ?? undefined,
      installmentEndDate: stepData.tuitionContract.installmentEndDate ?? undefined,
      thirdPartyName: stepData.tuitionContract.thirdPartyName ?? undefined,
      documentId: stepData.tuitionContract.documentId ?? undefined,
      documentResourceId: stepData.tuitionContract.documentResourceId ?? undefined,
      termsAcknowledged: Boolean(stepData.tuitionContract.termsAcknowledged),
    };
  }
  if (stepData.transportationB6t) {
    mapped.transportationB6t = {
      requiresTransportation: Boolean(stepData.transportationB6t.requiresTransportation),
      amPickupMode: stepData.transportationB6t.amPickupMode,
      amPickupAddress: stepData.transportationB6t.amPickupAddress ?? undefined,
      pmDropoffMode: stepData.transportationB6t.pmDropoffMode,
      pmDropoffAddress: stepData.transportationB6t.pmDropoffAddress ?? undefined,
      specialInstructions: stepData.transportationB6t.specialInstructions ?? undefined,
      signerFullName: stepData.transportationB6t.signerFullName ?? undefined,
      signedAt: toUnixSeconds(stepData.transportationB6t.signedAt) ?? undefined,
    };
  }
  if (stepData.registrationFee) {
    mapped.registrationFee = {
      amountPaidCents: Math.max(0, Math.round(Number(stepData.registrationFee.amountPaidCents || 0))),
      currency: (stepData.registrationFee.currency || 'USD').toUpperCase().slice(0, 3),
      paymentProvider: stepData.registrationFee.paymentProvider ?? 'other',
      transactionReference: stepData.registrationFee.transactionReference ?? undefined,
      receiptDocumentId: stepData.registrationFee.receiptDocumentId ?? undefined,
      paidAt: toUnixSeconds(stepData.registrationFee.paidAt) ?? undefined,
    };
  }
  return Object.keys(mapped).length > 0 ? mapped : undefined;
}

export function mapUiNoteToDomain(note: UiNote): { content: string; isImportant: boolean; authorName: string } {
  return {
    content: note.text,
    isImportant: note.isImportant,
    authorName: note.author,
  };
}

export function mapUiDocumentToDomain(doc: UiDocument): Omit<UploadDocumentInput, 'applicationId'> {
  const sizeBytes = Number.isFinite(doc.sizeBytes) && Number(doc.sizeBytes) >= 0
    ? Math.round(Number(doc.sizeBytes))
    : undefined;
  const childApplicationIds = Array.isArray(doc.childApplicationIds)
    ? doc.childApplicationIds
      .map((value) => String(value || '').trim())
      .filter(Boolean)
    : [];

  return {
    resourceId: ResourceId.parse(doc.resourceId),
    category: 'other',
    ...(doc.name ? { fileName: doc.name } : {}),
    ...(doc.contentType ? { contentType: doc.contentType } : {}),
    ...(sizeBytes != null ? { sizeBytes } : {}),
    ...(childApplicationIds.length > 0
      ? {
        childApplicationIds: childApplicationIds as NonNullable<UploadDocumentInput['childApplicationIds']>,
      }
      : {}),
    ...(doc.storageProvider ? { storageProvider: doc.storageProvider } : {}),
    ...(doc.storageKey ? { storageKey: doc.storageKey } : {}),
    ...(doc.storageBucket ? { storageBucket: doc.storageBucket } : {}),
    ...(doc.storageRegion ? { storageRegion: doc.storageRegion } : {}),
  };
}
